import styled from 'styled-components';

import NotificationStyled from '../../../../../components/Notification';
import StickyNav from '../../../../../components/StickyNav';
import { IconInfoCircle as InfoCircle } from '../../../../../core';
import UseCasePalettes from '../../../components/UseCasePalettes';

interface Props {
  className?: string;
}

const UseCaseTab = ({ className = '' }: Props) => (
  <div className={className}>
    <div className="tab-content">
      <div className="definition">
        <h2 className="title" id="definition">
          Definition
        </h2>
        <p>
          A horizontal grouped lollipop chart displays multiple data series as horizontal lines with
          circular markers at the end, grouped by category. This chart type is useful for comparing
          several variables across different categories while providing a clean and visually
          appealing design, especially when category labels are long. It allows for easy comparison
          of subcategories within each group.
        </p>
      </div>
      <div className="usage">
        <h2 className="title" id="usage">
          Usage
        </h2>
        <p className="subtitle">Horizontal grouped lollipop charts are a great choice for:</p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Comparing Multiple Variables with Long Labels:</strong> The horizontal layout is
            ideal for categories with lengthy labels, such as department names or survey questions,
            while enabling side-by-side comparisons of multiple data series.
          </li>
          <li>
            <strong>Highlighting Differences Within Groups:</strong> Grouped lollipop charts allow
            clear visualization of variations across multiple data points within each category, such
            as revenue comparisons for different years across regions.
          </li>
          <li>
            <strong>Providing a Modern and Space-Efficient Design:</strong> The clean and compact
            design of horizontal grouped lollipop charts makes them suitable for presenting grouped
            data clearly, even in reports with space constraints.
          </li>
        </ul>
      </div>
      <div className="limitations">
        <h2 className="title" id="limitations">
          Limitations
        </h2>
        <p className="subtitle">
          Horizontal grouped lollipop charts can be a great visualization tool, but they come with
          certain constraints that users should be aware of:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Visual Clutter with Many Groups or Categories:</strong> When there are a large
            number of groups or categories, the chart can become overcrowded, making it difficult to
            interpret the data accurately and reducing its overall readability.
          </li>
          <li>
            <strong>Limited Space for Labeling:</strong> Horizontal grouped lollipop charts require
            adequate space for category labels, especially if the group names or data points are
            lengthy, which can result in overlapping or truncated text.
          </li>
          <li>
            <strong>Challenging Comparison of Close Values:</strong> When data points within a group
            are close in value, it can be hard to discern small differences, potentially leading to
            misinterpretation or an inability to highlight subtle trends.
          </li>
        </ul>
      </div>
      <div className="colors">
        <h2 className="title" id="colors">
          Colors
        </h2>
        <p>
          For horizontal grouped lollipop charts, color selection helps distinguish between
          different groups, making comparisons straightforward. Using distinct colors for each group
          enhances clarity, while gradients can show trends or variations. Proper color choices
          ensure the chart is visually appealing and easy to interpret without overwhelming the
          viewer.
        </p>
        <NotificationStyled
          icon={<InfoCircle />}
          title="For in-depth information on this topic, please refer to the Colors page."
          color="blue"
          className="info-notification"
        />
        <UseCasePalettes palettes={['qualitative']} />
      </div>

      <div className="challenges">
        <h2 className="title">Challenges</h2>
        <p>
          Horizontal grouped lollipop charts are a visually appealing way to compare grouped data
          across categories, but they face distinct challenges that must be addressed:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Overlapping Groups:</strong> With many groups or closely spaced data points, the
            chart can appear cluttered, reducing clarity and visual impact.
          </li>
          <li>
            <strong>Label Crowding:</strong> Horizontal layout can lead to label congestion,
            particularly when category names are lengthy, making the chart harder to read.
          </li>
          <li>
            <strong>Comparison Difficulty:</strong> Small variations between groups or within the
            same category can be challenging to discern due to the proximity of data points.
          </li>
        </ul>
      </div>
    </div>
    <div>
      <StickyNav title="Contents" />
    </div>
  </div>
);

const StyledUseCaseTab = styled(UseCaseTab)`
  display: flex;
  gap: 2.5rem;
  justify-content: space-between;

  > .tab-content {
    width: 100%;
    .usage,
    .definition,
    .limitations,
    .challenges {
      max-width: 700px;
    }
    > .colors {
      > .info-notification {
        margin-top: 24px;
        margin-bottom: 40px;
      }

      > .palette {
        > .palette-title {
          color: ${({ theme }) => theme.neutralText};
          font-size: ${({ theme }) => theme.fontSizes.base};
          line-height: ${({ theme }) => theme.lineHeights.base};
          margin-bottom: 4px;
        }
      }

      .color-palette {
        margin-top: 12px;
      }
    }

    > .challenges {
      > .ordered-list-badge-num {
        margin-top: 16px;
      }
    }

    .title {
      color: ${({ theme }) => theme.neutralText};
      font-weight: 600;
      font-size: ${({ theme }) => theme.fontSizes.lg};
      line-height: ${({ theme }) => theme.lineHeights.lg};
      margin-bottom: 8px;
      margin-top: 40px;
    }

    p {
      color: ${({ theme }) => theme.neutralTextWeak};
      font-size: ${({ theme }) => theme.fontSizes.sm};
      line-height: ${({ theme }) => theme.lineHeights.sm};
      font-weight: 400;
      max-width: 700px;
    }

    .subtitle {
      margin-bottom: 16px;
    }

    // styles for list with badge
    .ordered-list-badge-num {
      font-size: 0.875rem;
      line-height: 1.4285714286;
      margin-left: 29px;
      counter-reset: num;
      list-style: none;
      color: ${({ theme }) => theme.neutralTextWeak};

      li {
        position: relative;
        margin-bottom: 16px;

        &::before {
          content: counter(num);
          counter-increment: num;
          position: absolute;
          left: -1.75rem;
          top: 0;
          width: 1.25rem;
          height: 1.25rem;
          border-radius: 0.1875rem;
          text-align: center;
          font-size: 0.6875rem;
          line-height: 1.8;
          color: ${({ theme }) => theme.primaryText};
          background-color: ${({ theme }) => theme.primaryBackgroundActive};
        }
      }
    }
  }
`;

export default StyledUseCaseTab;
